export const FREIGHT_PAYMENT_MODES = [
  {
    value: "PARTY_ADVANCE_WE_PAY_BALANCE",
    label: "Party advance — we pay balance to transporter",
  },
  {
    value: "BCPL_PAYS_FULL",
    label: "We pay full freight to transporter",
  },
  {
    value: "PARTY_PAID_FULL",
    label: "Party paid full freight to transporter",
  },
];

/** Who BCPL / the voucher books freight TDS against (PAN source). */
export const FREIGHT_PAYEE_OPTIONS = [
  { value: "TRANSPORT_COMPANY", label: "Transport company (bilty)" },
  { value: "TRANSPORTER", label: "Transporter (assigned truck)" },
  { value: "TRUCK_OWNER", label: "Truck owner" },
  { value: "PARTY", label: "Party / supplier" },
];

export function defaultFreightPayee(paymentMode) {
  return String(paymentMode || "").toUpperCase() === "PARTY_PAID_FULL"
    ? "PARTY"
    : "TRANSPORT_COMPANY";
}

/** Resolve TDS PAN from freight payee + linked parties / owner fields. */
export function resolveFreightTdsPan(slip = {}, purchase = null) {
  const kind = String(slip.freight_payee || defaultFreightPayee(slip.payment_mode) || "TRANSPORT_COMPANY").toUpperCase();
  if (kind === "PARTY") {
    return (
      slip.party_pan
      || purchase?.party_pan
      || purchase?.pan
      || purchase?.party?.pan
      || ""
    ).trim().toUpperCase();
  }
  if (kind === "TRUCK_OWNER") {
    return (slip.truck_owner_pan || "").trim().toUpperCase();
  }
  if (kind === "TRANSPORTER") {
    return (
      slip.transporter_pan
      || ""
    ).trim().toUpperCase();
  }
  return (
    slip.transport_company_pan
    || ""
  ).trim().toUpperCase();
}

export const FREIGHT_BILLABLE_BASIS_OPTIONS = [
  { value: "UNLOAD", label: "Unload weight (our weighbridge)" },
  { value: "LOAD", label: "Load weight (party weighbridge)" },
];

export const SHORTAGE_ALLOWANCE_MODES = [
  { value: "GM_PER_BAG", label: "Grams per bag", unit: "gm/bag", placeholder: "e.g. 200" },
  { value: "PER_QTL", label: "Grams per qtl", unit: "gm/qtl", placeholder: "e.g. 50" },
  { value: "NET_TRUCK_KG", label: "Net truck", unit: "kg", placeholder: "e.g. 100" },
];

export const DALA_RATE_PER_QTL = 2.5;

export const DALA_WEIGHT_BASIS_OPTIONS = [
  { value: "UNLOAD", label: "Unload weight (our weighbridge net)" },
  { value: "BILLABLE", label: "Billable weight (after bardana)" },
];

export function freightModeHelp(deliveryCondition) {
  const delivery = String(deliveryCondition || "TRUCK_LOAD").toUpperCase();
  if (delivery === "FOR") {
    return "FOR: amounts we pay are deducted from supplier bill; party-paid freight needs no bill adjustment.";
  }
  return "TRUCK_LOAD: party advance or full party payment is added to supplier bill; our payment is our cost.";
}

/** Parse YYYY-MM-DD (or ISO datetime prefix) to Date at local midnight. */
export function parseIsoDateOnly(value) {
  if (!value) return null;
  const text = String(value).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null;
  const [y, m, d] = text.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
}

/**
 * Day-based halting from reached → unload (reached day counts as free-day 1).
 * free 2 → reached + next day free; day after tomorrow is chargeable.
 * chargeable = max(0, (unload − reached).days + 1 − freeDays)
 */
export function computeHaltingSettlement({
  halting_applicable,
  load_date,
  unload_date,
  reached_date,
  halting_free_days,
  halting_rate_per_day,
  halting_charges,
  halting_days,
} = {}) {
  const applicable = Boolean(halting_applicable);
  const freeDays = Math.max(0, Number(halting_free_days) || 0);
  const rate = Math.max(0, Number(halting_rate_per_day) || 0);
  const reached = parseIsoDateOnly(reached_date);
  const unload = parseIsoDateOnly(unload_date);

  if (!applicable) {
    return {
      halting_applicable: false,
      halting_free_days: freeDays,
      halting_rate_per_day: rate,
      halting_elapsed_days: 0,
      halting_days: 0,
      halting_charges: Number(halting_charges) || 0,
      reached_date: reached_date || "",
      load_date: load_date || "",
    };
  }

  if (reached && unload) {
    // Inclusive calendar days at destination
    const elapsed = Math.max(0, Math.round((unload - reached) / 86400000) + 1);
    const chargeable = Math.max(0, elapsed - freeDays);
    return {
      halting_applicable: true,
      halting_free_days: freeDays,
      halting_rate_per_day: rate,
      halting_elapsed_days: elapsed,
      halting_days: chargeable,
      halting_charges: Math.round(chargeable * rate * 100) / 100,
      reached_date: reached_date || "",
      load_date: load_date || "",
    };
  }

  const days = Math.max(0, Number(halting_days) || 0);
  let charges = Number(halting_charges) || 0;
  if (days && rate && !charges) charges = Math.round(days * rate * 100) / 100;
  return {
    halting_applicable: true,
    halting_free_days: freeDays,
    halting_rate_per_day: rate,
    halting_elapsed_days: 0,
    halting_days: days,
    halting_charges: charges,
    reached_date: reached_date || "",
    load_date: load_date || "",
  };
}

/** Patch freight form fields after any halting input change. */
export function applyHaltingPatch(form, patch) {
  const next = { ...form, ...patch };
  if (Object.prototype.hasOwnProperty.call(patch, "halting_applicable") && !patch.halting_applicable) {
    next.halting_days = "0";
    next.halting_charges = "0";
    return next;
  }
  const halt = computeHaltingSettlement({
    halting_applicable: next.halting_applicable,
    load_date: next.load_date,
    unload_date: next.unload_date,
    reached_date: next.reached_date,
    halting_free_days: next.halting_free_days,
    halting_rate_per_day: next.halting_rate_per_day,
    halting_charges: next.halting_charges,
    halting_days: next.halting_days,
  });
  if (halt.halting_applicable && next.reached_date && next.unload_date) {
    next.halting_days = String(halt.halting_days);
    next.halting_charges = String(halt.halting_charges);
  }
  return next;
}

export function shortageAllowanceModeMeta(mode) {
  return SHORTAGE_ALLOWANCE_MODES.find((o) => o.value === mode) || null;
}

export function defaultFreightForm(voucher, preview = null) {
  const fd = preview?.form_defaults || {};
  const slip = voucher?.freight_slip || preview?.freight_slip || {};
  const payment = voucher?.freight_payment || preview?.freight_payment || {};
  const partyAdvance = fd.party_advance ?? fd.advance ?? slip.party_advance ?? slip.advance ?? 0;
  const transporterName =
    fd.transporter_owner_name
    ?? slip.transporter_owner_name
    ?? slip.transporter_name
    ?? "";
  const companyName =
    fd.transport_company_name
    ?? slip.transport_company_name
    ?? slip.transport_company_display_name
    ?? transporterName
    ?? "";
  const sameAs = Boolean(
    fd.transporter_same_as_company
    ?? slip.transporter_same_as_company
    ?? true,
  );
  return {
    transporter_owner_name: transporterName,
    transporter_party_id: fd.transporter_party_id ?? slip.transporter_party_id ?? null,
    transporter_display_name: fd.transporter_display_name ?? slip.transporter_display_name ?? "",
    transporter_name: fd.transporter_name ?? slip.transporter_name ?? transporterName,
    transport_company_party_id:
      fd.transport_company_party_id
      ?? slip.transport_company_party_id
      ?? (sameAs ? (fd.transporter_party_id ?? slip.transporter_party_id ?? null) : null),
    transport_company_name: companyName,
    transport_company_display_name:
      fd.transport_company_display_name ?? slip.transport_company_display_name ?? companyName,
    transporter_same_as_company: sameAs,
    bank_name: fd.bank_name ?? slip.bank_name ?? "",
    bank_account_no: fd.bank_account_no ?? slip.bank_account_no ?? "",
    bank_ifsc: fd.bank_ifsc ?? slip.bank_ifsc ?? "",
    transport_company_pan: fd.transport_company_pan ?? slip.transport_company_pan ?? "",
    transporter_pan: fd.transporter_pan ?? slip.transporter_pan ?? "",
    truck_owner_name: fd.truck_owner_name ?? slip.truck_owner_name ?? "",
    truck_owner_pan: fd.truck_owner_pan ?? slip.truck_owner_pan ?? "",
    freight_payee: fd.freight_payee ?? slip.freight_payee ?? defaultFreightPayee(fd.payment_mode ?? slip.payment_mode ?? payment.mode),
    pan_no: fd.pan_no ?? slip.pan_no ?? "",
    tds_payee_category: fd.tds_payee_category ?? slip.tds_payee_category ?? "",
    tds_small_transporter_exempt: Boolean(
      fd.tds_small_transporter_exempt ?? slip.tds_small_transporter_exempt,
    ),
    tds_manual_override: Boolean(fd.tds_manual_override ?? slip.tds_manual_override),
    load_date: fd.load_date || slip.load_date || "",
    unload_date: fd.unload_date || slip.unload_date || "",
    from_place: fd.from_place || slip.from_place || "",
    to_place: fd.to_place || slip.to_place || "ANUPSHAHR",
    bilty_number: fd.bilty_number ?? slip.bilty_number ?? "",
    carrier_name: fd.carrier_name ?? slip.carrier_name ?? companyName,
    freight_rate_per_qtl: fd.freight_rate_per_qtl ?? slip.freight_rate_per_qtl ?? voucher?.rate_per_qtl ?? "",
    other_charges: fd.other_charges ?? slip.other_charges ?? 0,
    party_advance: partyAdvance,
    advance: partyAdvance,
    tds_pct: fd.tds_pct ?? slip.tds_pct ?? "",
    clerk_bilty_charge: fd.clerk_bilty_charge ?? slip.clerk_bilty_charge ?? 0,
    deduct_dala: Boolean(slip.deduct_dala),
    dala_weight_basis: slip.dala_weight_basis === "UNLOAD" ? "UNLOAD" : "BILLABLE",
    other_deductions: fd.other_deductions ?? slip.other_deductions ?? 0,
    payment_mode: fd.payment_mode ?? payment.mode ?? "BCPL_PAYS_FULL",
    accounts_note: voucher?.deductions?.accounts_note || "",
  };
}

export function buildFreightPreviewParams(form) {
  const params = {};
  const numeric = [
    "freight_rate_per_qtl",
    "other_charges",
    "party_advance",
    "advance",
    "clerk_bilty_charge",
    "other_deductions",
  ];
  for (const key of numeric) {
    if (form[key] !== "" && form[key] != null) params[key] = Number(form[key]);
  }
  if (form.tds_manual_override && form.tds_pct !== "" && form.tds_pct != null) {
    params.tds_pct = Number(form.tds_pct);
    params.tds_manual_override = true;
  }
  if (params.party_advance != null && params.advance == null) {
    params.advance = params.party_advance;
  }
  if (form.deduct_dala) params.deduct_dala = true;
  else params.deduct_dala = false;
  if (form.dala_weight_basis) params.dala_weight_basis = form.dala_weight_basis;
  if (form.tds_small_transporter_exempt) params.tds_small_transporter_exempt = true;
  else params.tds_small_transporter_exempt = false;
  for (const key of [
    "transporter_owner_name",
    "transporter_party_id",
    "transporter_name",
    "transporter_display_name",
    "transport_company_party_id",
    "transport_company_name",
    "transport_company_display_name",
    "bank_name",
    "bank_account_no",
    "bank_ifsc",
    "pan_no",
    "truck_owner_name",
    "truck_owner_pan",
    "transport_company_pan",
    "transporter_pan",
    "party_pan",
    "freight_payee",
    "tds_payee_category",
    "load_date",
    "unload_date",
    "from_place",
    "to_place",
    "payment_mode",
    "bilty_number",
    "carrier_name",
  ]) {
    if (form[key] !== "" && form[key] != null) params[key] = form[key];
  }
  if (form.transporter_same_as_company != null) {
    params.transporter_same_as_company = Boolean(form.transporter_same_as_company);
  }
  // Carrier ≡ transport company (bilty payee), not the commission transporter
  const carrier = (
    form.carrier_name
    || form.transport_company_name
    || form.transport_company_display_name
    || form.bilty_transporter_name
    || ""
  ).trim();
  if (carrier) {
    params.carrier_name = carrier;
    params.bilty_transporter_name = carrier;
  }
  const tdsPan = resolveFreightTdsPan(form);
  if (tdsPan) params.pan_no = tdsPan;
  return params;
}

export function buildFreightFinalizePayload(form) {
  const payload = buildFreightPreviewParams(form);
  if (form.accounts_note?.trim()) payload.accounts_note = form.accounts_note.trim();
  return payload;
}

/** Defaults for freight slip fields during pending purchase review (before voucher exists). */
export function defaultFreightSlipForPurchase(purchase, preview = null) {
  const fd = preview?.freight_form_defaults || {};
  const slip = preview?.freight_slip || {};
  const partyAdvance = fd.party_advance ?? fd.advance ?? slip.party_advance ?? slip.advance ?? 0;
  const legacyTransporterName = (
    fd.transporter_owner_name
    || slip.transporter_owner_name
    || slip.transporter_name
    || ""
  ).trim();
  const companyName = (
    fd.transport_company_name
    || slip.transport_company_name
    || slip.transport_company_display_name
    || ""
  ).trim();
  const driverName = (
    purchase?.driver_name
    || purchase?.gate_pass?.driver_name
    || ""
  ).trim();
  // Truck owner from history / saved slip — never gate-pass driver.
  let truckOwnerName = (fd.truck_owner_name || slip.truck_owner_name || "").trim();
  if (!truckOwnerName && legacyTransporterName) {
    const legacy = legacyTransporterName;
    const isDriver = driverName && legacy.toLowerCase() === driverName.toLowerCase();
    const isCompany = companyName && legacy.toLowerCase() === companyName.toLowerCase();
    if (!isDriver && !isCompany) {
      truckOwnerName = legacy;
    }
  }
  const sameAs = fd.transporter_same_as_company != null
    ? Boolean(fd.transporter_same_as_company)
    : slip.transporter_same_as_company != null
      ? Boolean(slip.transporter_same_as_company)
      : true;
  const companyPartyId = fd.transport_company_party_id ?? slip.transport_company_party_id ?? null;
  const base = {
    transport_company_party_id: companyPartyId,
    transport_company_name: companyName,
    transport_company_display_name:
      fd.transport_company_display_name ?? slip.transport_company_display_name ?? companyName,
    transporter_same_as_company: sameAs,
    transporter_party_id: sameAs
      ? companyPartyId
      : (fd.transporter_party_id ?? slip.transporter_party_id ?? null),
    transporter_owner_name: sameAs
      ? companyName
      : (fd.transporter_owner_name ?? slip.transporter_owner_name ?? ""),
    transporter_display_name: sameAs
      ? companyName
      : (fd.transporter_display_name ?? slip.transporter_display_name ?? ""),
    transporter_name: sameAs
      ? companyName
      : (fd.transporter_name ?? slip.transporter_name ?? ""),
    bank_name: fd.bank_name ?? slip.bank_name ?? "",
    bank_account_no: fd.bank_account_no ?? slip.bank_account_no ?? "",
    bank_ifsc: fd.bank_ifsc ?? slip.bank_ifsc ?? "",
    transport_company_pan: fd.transport_company_pan ?? slip.transport_company_pan ?? "",
    transporter_pan: sameAs
      ? (fd.transport_company_pan ?? slip.transport_company_pan ?? slip.transporter_pan ?? "")
      : (fd.transporter_pan ?? slip.transporter_pan ?? ""),
    truck_owner_name: truckOwnerName,
    truck_owner_pan: fd.truck_owner_pan ?? slip.truck_owner_pan ?? "",
    party_pan: fd.party_pan ?? slip.party_pan ?? purchase?.party_pan ?? "",
    freight_payee:
      fd.freight_payee
      ?? slip.freight_payee
      ?? defaultFreightPayee(fd.payment_mode ?? slip.payment_mode),
    pan_no: fd.pan_no ?? slip.pan_no ?? "",
    tds_payee_category: fd.tds_payee_category ?? slip.tds_payee_category ?? "",
    tds_small_transporter_exempt: Boolean(
      fd.tds_small_transporter_exempt ?? slip.tds_small_transporter_exempt,
    ),
    tds_manual_override: Boolean(fd.tds_manual_override ?? slip.tds_manual_override),
    load_date:
      fd.load_date
      || slip.load_date
      || (purchase?.invoice_date || "").slice(0, 10)
      || "",
    unload_date:
      fd.unload_date
      || slip.unload_date
      || (purchase?.unloading?.created_at || "").slice(0, 10)
      || (purchase?.tare_weighed_at || "").slice(0, 10)
      || "",
    from_place:
      fd.from_place
      || slip.from_place
      || purchase?.party_city
      || purchase?.party?.city
      || "",
    to_place: fd.to_place || slip.to_place || "ANUPSHAHR",
    bilty_number: fd.bilty_number ?? slip.bilty_number ?? "",
    carrier_name: fd.carrier_name ?? slip.carrier_name ?? companyName,
    freight_rate_per_qtl: fd.freight_rate_per_qtl ?? slip.freight_rate_per_qtl ?? "",
    other_charges: fd.other_charges ?? slip.other_charges ?? 0,
    party_advance: partyAdvance,
    advance: partyAdvance,
    tds_pct: fd.tds_pct ?? slip.tds_pct ?? "",
    clerk_bilty_charge: fd.clerk_bilty_charge ?? slip.clerk_bilty_charge ?? 0,
    deduct_dala: Boolean(slip.deduct_dala),
    dala_weight_basis: slip.dala_weight_basis === "UNLOAD" ? "UNLOAD" : "BILLABLE",
    other_deductions: fd.other_deductions ?? slip.other_deductions ?? 0,
    payment_mode: fd.payment_mode ?? preview?.freight_payment?.mode ?? "BCPL_PAYS_FULL",
    accounts_note: "",
  };
  return {
    ...base,
    pan_no: base.pan_no || resolveFreightTdsPan(base, purchase),
    carrier_name: base.carrier_name || companyName,
  };
}

export function purchaseFreightPreviewReady(form) {
  const calcMode =
    form?.freight_calc_mode
    || (
      form?.freight_amount !== "" && form?.freight_amount != null
        ? "FIXED"
        : "PER_QTL"
    );
  if (calcMode === "FIXED") {
    const amount = form?.freight_amount;
    return amount !== "" && amount != null && Number(amount) > 0;
  }
  const slipRate = form?.freight_slip?.freight_rate_per_qtl;
  const topRate = form?.freight_rate_per_qtl;
  const rate = topRate !== "" && topRate != null ? topRate : slipRate;
  return rate !== "" && rate != null && Number(rate) > 0;
}

/** Material invoice ₹/qtl = total net payable ÷ total billable weight (for short-goods deduction). */
export function goodsInvoiceRateFromAllocations(allocations) {
  if (!allocations?.length) return undefined;
  let totalNet = 0;
  let totalBillable = 0;
  for (const a of allocations) {
    totalNet += Number(a.net_payable || 0);
    totalBillable += Number(a.billable_qtl || 0);
  }
  if (totalBillable <= 0) return undefined;
  return Math.round((totalNet / totalBillable) * 10000) / 10000;
}

/** Prefer truck-level material net (incl. party expenses) over stripped per-alloc nets. */
export function goodsInvoiceRateFromMaterialNet(materialNetPayable, billableQtl) {
  const net = Number(materialNetPayable);
  const billable = Number(billableQtl);
  if (!(billable > 0) || !Number.isFinite(net)) return undefined;
  return Math.round((net / billable) * 10000) / 10000;
}

function appendPurchaseFreightOptions(params, purchaseForm) {
  if (purchaseForm?.freight_billable_basis) {
    params.freight_billable_basis = purchaseForm.freight_billable_basis;
  }
  if (purchaseForm?.shortage_allowance_mode) {
    params.shortage_allowance_mode = purchaseForm.shortage_allowance_mode;
    if (
      purchaseForm.shortage_allowance_value !== ""
      && purchaseForm.shortage_allowance_value != null
    ) {
      params.shortage_allowance_value = Number(purchaseForm.shortage_allowance_value);
    }
  } else if (purchaseForm?.freight_allowance_code) {
    params.freight_allowance_code = purchaseForm.freight_allowance_code;
  }
  if (purchaseForm?.freight_slip?.deduct_dala) {
    params.deduct_dala = true;
  } else {
    params.deduct_dala = false;
  }
  if (purchaseForm?.freight_slip?.dala_weight_basis) {
    params.dala_weight_basis = purchaseForm.freight_slip.dala_weight_basis;
  }
}

export function buildPurchaseFreightPreviewParams(purchaseForm, settlementPreview = null) {
  const slip = purchaseForm?.freight_slip || {};
  const params = buildFreightPreviewParams({
    ...slip,
    freight_rate_per_qtl:
      purchaseForm?.freight_rate_per_qtl !== "" && purchaseForm?.freight_rate_per_qtl != null
        ? purchaseForm.freight_rate_per_qtl
        : slip.freight_rate_per_qtl,
  });
  if (purchaseForm?.freight_amount !== "" && purchaseForm?.freight_amount != null) {
    params.freight_amount = Number(purchaseForm.freight_amount);
  }
  if (purchaseForm?.party_net_weight_qtl !== "" && purchaseForm?.party_net_weight_qtl != null) {
    params.party_net_weight_qtl = Number(purchaseForm.party_net_weight_qtl);
  }
  if (purchaseForm?.deduction_scope) {
    params.deduction_scope = purchaseForm.deduction_scope;
  }
  appendPurchaseFreightOptions(params, purchaseForm);
  const billableFromAllocs = (settlementPreview?.allocations || []).reduce(
    (sum, a) => sum + Number(a.billable_qtl || 0),
    0,
  );
  const materialNet =
    settlementPreview?.material_net_before_freight_ledger
    ?? settlementPreview?.material_net_payable
    ?? null;
  const goodsRate =
    goodsInvoiceRateFromMaterialNet(materialNet, billableFromAllocs)
    ?? goodsInvoiceRateFromAllocations(settlementPreview?.allocations);
  if (goodsRate != null) {
    params.goods_invoice_rate_per_qtl = goodsRate;
  }
  return params;
}

export function buildPurchaseFreightFinalizePayload(purchaseForm) {
  const slip = purchaseForm?.freight_slip || {};
  const all = buildPurchaseFreightPreviewParams(purchaseForm);
  const slipKeys = [
    "transporter_owner_name",
    "transporter_party_id",
    "transporter_name",
    "transporter_display_name",
    "transport_company_party_id",
    "transport_company_name",
    "transport_company_display_name",
    "transporter_same_as_company",
    "bank_name",
    "bank_account_no",
    "bank_ifsc",
    "pan_no",
    "truck_owner_name",
    "truck_owner_pan",
    "transport_company_pan",
    "transporter_pan",
    "party_pan",
    "freight_payee",
    "tds_payee_category",
    "tds_small_transporter_exempt",
    "tds_manual_override",
    "load_date",
    "unload_date",
    "from_place",
    "to_place",
    "freight_rate_per_qtl",
    "other_charges",
    "advance",
    "party_advance",
    "tds_pct",
    "clerk_bilty_charge",
    "other_deductions",
    "payment_mode",
    "deduct_dala",
    "dala_weight_basis",
    "bilty_number",
    "carrier_name",
    "bilty_transporter_name",
    "accounts_note",
  ];
  const payload = {};
  for (const key of slipKeys) {
    if (all[key] !== undefined) payload[key] = all[key];
  }
  payload.deduct_dala = Boolean(slip.deduct_dala);
  payload.dala_weight_basis = slip.dala_weight_basis === "UNLOAD" ? "UNLOAD" : "BILLABLE";
  payload.tds_small_transporter_exempt = Boolean(slip.tds_small_transporter_exempt);
  payload.tds_manual_override = Boolean(slip.tds_manual_override);
  payload.transporter_same_as_company = Boolean(slip.transporter_same_as_company ?? true);
  // Carrier ≡ transport company (bilty payee)
  const carrier = (
    slip.carrier_name
    || slip.transport_company_name
    || slip.transport_company_display_name
    || slip.bilty_transporter_name
    || payload.carrier_name
    || ""
  ).trim();
  if (carrier) {
    payload.carrier_name = carrier;
    payload.bilty_transporter_name = carrier;
  }
  const tdsPan = resolveFreightTdsPan(slip);
  if (tdsPan) payload.pan_no = tdsPan;
  if (slip.accounts_note?.trim()) payload.accounts_note = slip.accounts_note.trim();
  return payload;
}
